import { useEffect, useRef, useState, useCallback } from "react";
import api, { mediaUrl } from "../lib/api";
import { AVMark } from "../components/Logo";
import { Scoreboard, GoalCelebration } from "../components/Scoreboard";
import { APP_VERSION } from "../version";
import { MonitorPlay, Wifi } from "lucide-react";

export default function Player() {
  const [tvId, setTvId] = useState(localStorage.getItem("tv_id"));
  const [state, setState] = useState(null); // sync response
  const [index, setIndex] = useState(0);
  const [gapActive, setGapActive] = useState(false);
  const [audioUnlocked, setAudioUnlocked] = useState(false);
  const itemsRef = useRef("");
  const lastLogged = useRef(null);
  const lastRefresh = useRef(null);
  const videoRef = useRef(null);
  const videoTimer = useRef(null);
  const lastOk = useRef(Date.now());
  const lastGoalTs = useRef(0);
  const [goalTeam, setGoalTeam] = useState(null);

  // Fire a confetti/fireworks celebration whenever the backend reports a new goal
  const sports = state?.sports;
  useEffect(() => {
    const g = sports?.goal;
    if (g && g.ts && g.ts !== lastGoalTs.current) {
      lastGoalTs.current = g.ts;
      setGoalTeam(g.team || "");
    }
  }, [sports?.goal]);

  // Auto-dismiss the celebration ~5.5s after it appears. Kept in its own effect so the
  // 5s sync poll (which re-creates sports.goal each time) can't cancel the hide timer.
  useEffect(() => {
    if (goalTeam === null) return;
    const t = setTimeout(() => setGoalTeam(null), 5500);
    return () => clearTimeout(t);
  }, [goalTeam]);

  // Unlock audio on first user interaction (browsers block unmuted autoplay)
  useEffect(() => {
    const unlock = () => setAudioUnlocked(true);
    window.addEventListener("click", unlock);
    window.addEventListener("keydown", unlock);
    return () => { window.removeEventListener("click", unlock); window.removeEventListener("keydown", unlock); };
  }, []);

  // Keep the screen awake (browsers / kiosk WebView)
  useEffect(() => {
    let lock = null;
    const acquire = async () => {
      try {
        if ("wakeLock" in navigator) lock = await navigator.wakeLock.request("screen");
      } catch (e) { /* ignore */ }
    };
    acquire();
    const onVisible = () => { if (document.visibilityState === "visible") acquire(); };
    document.addEventListener("visibilitychange", onVisible);
    return () => {
      document.removeEventListener("visibilitychange", onVisible);
      if (lock) lock.release().catch(() => {});
    };
  }, []);

  // Register device once
  useEffect(() => {
    if (tvId) return;
    api.post("/tv/register").then((r) => {
      localStorage.setItem("tv_id", r.data.tv_id);
      setTvId(r.data.tv_id);
    });
  }, [tvId]);

  // Poll sync
  const sync = useCallback(async () => {
    if (!tvId) return;
    try {
      const r = await api.get(`/tv/${tvId}/sync`, { params: { v: APP_VERSION } });
      setState(r.data);
      lastOk.current = Date.now();
      // Dashboard "Refresh all TVs" — reload to pick up new app code + content on demand
      if (r.data.refresh) {
        if (lastRefresh.current !== null && lastRefresh.current !== r.data.refresh) {
          window.location.reload();
          return;
        }
        lastRefresh.current = r.data.refresh;
      }
      const sig = JSON.stringify({ m: r.data.mode, i: r.data.items, x: r.data.interstitial, e: r.data.interstitial_every, s: !!r.data.sports, se: r.data.sports?.every });
      if (sig !== itemsRef.current) {
        itemsRef.current = sig;
        setIndex(0);
      }
    } catch (e) {
      if (e.response?.status === 404) {
        localStorage.removeItem("tv_id");
        setTvId(null);
      }
    }
  }, [tvId]);

  useEffect(() => {
    sync();
    const i = setInterval(sync, 5000);
    return () => clearInterval(i);
  }, [sync]);

  // Auto-reload so deploys propagate and any stuck/stale screen self-heals:
  //  - full reload every 20 min (picks up new app code without manual restart)
  //  - watchdog: if sync has failed for >2 min straight, reload to recover
  useEffect(() => {
    const periodic = setInterval(() => window.location.reload(), 20 * 60 * 1000);
    const watchdog = setInterval(() => {
      if (Date.now() - lastOk.current > 120000) window.location.reload();
    }, 30000);
    return () => { clearInterval(periodic); clearInterval(watchdog); };
  }, []);

  // Advance playback
  const baseItems = state?.items || [];
  const interstitial = state?.interstitial;
  const every = Math.max(state?.interstitial_every || 1, 1);
  // Insert the per-TV interstitial video after every N items
  let items = baseItems;
  if (interstitial && baseItems.length > 0) {
    items = [];
    baseItems.forEach((it, i) => {
      items.push(it);
      if ((i + 1) % every === 0) items.push(interstitial);
    });
    if (items.length === baseItems.length) items.push(interstitial); // ensure it still plays
  }
  // Weave a "grand" live-sports scoreboard slide into the rotation
  const sportsItem = sports && (sports.matches?.length)
    ? { type: "sports", media_id: "__sports__", duration: 14, gap: 0 }
    : null;
  if (sportsItem) {
    if (items.length === 0) {
      items = [sportsItem];
    } else {
      const everyS = Math.max(sports.every || 3, 1);
      const woven = [];
      items.forEach((it, i) => {
        woven.push(it);
        if ((i + 1) % everyS === 0) woven.push(sportsItem);
      });
      if (!woven.includes(sportsItem)) woven.push(sportsItem);
      items = woven;
    }
  }
  const itemCount = items.length;
  const current = items[index];
  // Stable primitives so the 5s sync poll (new object each time) doesn't reset timers
  const curMediaId = current?.media_id;
  const curType = current?.type;
  const curDuration = current?.duration ?? 10;
  const curGap = current?.gap ?? 0;

  const advance = () => setIndex((p) => (itemCount ? (p + 1) % itemCount : 0));

  // Gap (black pause) before each item shows
  useEffect(() => {
    if (!curMediaId) { setGapActive(false); return; }
    const gapMs = curGap * 1000;
    if (gapMs > 0) {
      setGapActive(true);
      const g = setTimeout(() => setGapActive(false), gapMs);
      return () => clearTimeout(g);
    }
    setGapActive(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [curMediaId, index, curGap]);

  useEffect(() => {
    if (!curMediaId || gapActive) return;
    // log play
    if (tvId && curType !== "sports" && lastLogged.current !== curMediaId + index) {
      lastLogged.current = curMediaId + index;
      api.post(`/tv/${tvId}/played`, { media_id: curMediaId }).catch(() => {});
    }
    // Images advance on a timer. Videos normally advance via onEnded, but we also
    // set a generous fallback timer so a stalled/failed video can't freeze the loop.
    if (curType === "video") {
      if (itemCount <= 1) return; // single video just loops
      videoTimer.current = setTimeout(advance, ((curDuration || 15) + 90) * 1000);
      return () => { if (videoTimer.current) { clearTimeout(videoTimer.current); videoTimer.current = null; } };
    }
    const ms = curDuration * 1000;
    const t = setTimeout(advance, ms);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [curMediaId, curType, curDuration, index, itemCount, tvId, gapActive]);

  // Apply volume / mute. Always start muted so the video autoplays on every TV
  // browser (no "tap to play" overlay), then unmute once playback has started.
  useEffect(() => {
    const v = videoRef.current;
    if (!v) return;
    v.volume = (state?.volume ?? 80) / 100;
    const wantSound = !(state?.muted ?? false);
    v.muted = true;
    const started = v.play?.();
    const tryUnmute = () => {
      if (!wantSound) return;
      v.muted = false;
      const p2 = v.play?.();
      if (p2 && p2.catch) p2.catch(() => { v.muted = true; v.play?.().catch(() => {}); });
    };
    if (started && started.then) started.then(tryUnmute).catch(() => {});
    else tryUnmute();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [curMediaId, gapActive, state?.volume, state?.muted, audioUnlocked]);

  // Fullscreen black
  if (!state) {
    return (
      <div className="fixed inset-0 bg-black flex items-center justify-center text-white">
        <Wifi className="w-8 h-8 animate-pulse text-primary" />
      </div>
    );
  }

  // Not paired -> pairing code
  if (!state.paired) {
    return (
      <div className="fixed inset-0 bg-black flex flex-col items-center justify-center text-white">
        <div className="absolute inset-0 opacity-[0.05]"
          style={{ backgroundImage: "radial-gradient(#007AFF 1px, transparent 1px)", backgroundSize: "40px 40px" }} />
        <div className="relative text-center">
          <div className="flex justify-center mb-8">
            <AVMark size={72} />
          </div>
          <p className="text-sm uppercase tracking-[0.3em] text-white/50 mb-4">Pairing Code</p>
          <div data-testid="pairing-code" className="font-mono text-7xl font-bold tracking-[0.25em] text-white">
            {state.pairing_code}
          </div>
          <p className="text-white/40 mt-8 text-sm">
            Enter this code in your SignHub dashboard → <span className="text-primary">Add TV</span>
          </p>
          <div className="flex items-center justify-center gap-2 mt-6 text-green-500 text-xs">
            <span className="w-2 h-2 rounded-full bg-green-500 live-dot" /> Connected, waiting to pair…
          </div>
        </div>
      </div>
    );
  }

  // Paired but no content
  if (!current) {
    return (
      <div className="fixed inset-0 bg-black flex flex-col items-center justify-center text-white">
        <MonitorPlay className="w-12 h-12 text-white/20 mb-4" />
        <p className="text-white/40">{state.name} · Ready</p>
        <p className="text-white/20 text-sm mt-1">Waiting for a playlist to be assigned…</p>
        {state.ticker && <Ticker text={state.ticker} size={state.ticker_size} />}
        {goalTeam !== null && <GoalCelebration team={goalTeam} />}
      </div>
    );
  }

  // Always fill the whole TV screen — no black bars / blurred pillarbox.
  const fitClass = "object-cover";

  return (
    <div className="fixed inset-0 bg-black overflow-hidden" data-testid="player-stage">
      {gapActive ? null : current.type === "sports" ? (
        <Scoreboard data={sports} />
      ) : current.type === "video" ? (
        <video
          ref={videoRef}
          key={current.media_id + index}
          src={mediaUrl(current.media_id)}
          className={`w-full h-full ${fitClass}`}
          autoPlay
          muted
          loop={itemCount === 1}
          onLoadedMetadata={(e) => {
            // Replace the generous fallback with a precise timer = real video length
            if (itemCount <= 1) return;
            if (videoTimer.current) clearTimeout(videoTimer.current);
            const d = e.target.duration;
            const ms = (isFinite(d) && d > 0 ? d * 1000 + 2000 : (curDuration + 5) * 1000);
            videoTimer.current = setTimeout(advance, ms);
          }}
          onEnded={() => {
            if (videoTimer.current) clearTimeout(videoTimer.current);
            if (itemCount > 1) { advance(); return; }
            // single video: force a clean restart (loop attribute can stall on TV decoders)
            const v = videoRef.current;
            if (v) { try { v.currentTime = 0; } catch (e) {} v.play?.().catch(() => {}); }
          }}
          onError={() => {
            // video failed to load (network/codec) — don't freeze; skip to next item
            if (videoTimer.current) clearTimeout(videoTimer.current);
            if (itemCount > 1) videoTimer.current = setTimeout(advance, 1500);
          }}
          playsInline
          preload="auto"
          webkit-playsinline="true"
          x5-playsinline="true"
        />
      ) : (
        <>
          {fitClass === "object-contain" && (
            <img
              src={mediaUrl(current.media_id)}
              alt=""
              aria-hidden="true"
              className="absolute inset-0 w-full h-full object-cover scale-110 blur-2xl opacity-70"
            />
          )}
          <img
            key={current.media_id + index}
            src={mediaUrl(current.media_id)}
            alt=""
            className={`relative w-full h-full ${fitClass}`}
          />
        </>
      )}
      {state.ticker && <Ticker text={state.ticker} size={state.ticker_size} />}
      {goalTeam !== null && <GoalCelebration team={goalTeam} />}
    </div>
  );
}

function Ticker({ text, size = "medium" }) {
  const fontSize = size === "large" ? "64px" : size === "small" ? "30px" : "46px";
  return (
    <div
      className="absolute bottom-0 inset-x-0 overflow-hidden text-white whitespace-nowrap"
      style={{
        fontSize,
        background: "linear-gradient(90deg,#05205c 0%,#0A84FF 45%,#19E3FF 100%)",
        borderTop: "2px solid rgba(255,255,255,0.3)",
        boxShadow: "0 -8px 30px rgba(10,132,255,0.55)",
      }}
    >
      <div
        className="absolute left-0 top-0 bottom-0 flex items-center gap-[0.35em] z-10 font-black tracking-[3px]"
        style={{ fontSize: "0.55em", background: "rgba(0,0,0,0.4)", padding: "0 0.7em" }}
      >
        <span className="rounded-full live-dot" style={{ width: "0.7em", height: "0.7em", background: "#FF3B30", boxShadow: "0 0 12px #FF3B30" }} />
        LIVE
      </div>
      <span
        className="ticker-track inline-block font-extrabold"
        style={{ paddingLeft: "100%", paddingTop: "0.4em", paddingBottom: "0.4em", textShadow: "0 2px 6px rgba(0,0,0,0.35)" }}
      >
        {text}
      </span>
    </div>
  );
}
